import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/Navbar';
import axiosInstance from '../../services/axiosInstance';

const cardBaseStyle = {
  background: 'linear-gradient(135deg, #0f172a 0%, #111827 60%, #0b8c6c 120%)',
  borderRadius: '18px',
  padding: '20px',
  color: '#e2e8f0',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.35)',
  border: '1px solid rgba(15, 118, 110, 0.25)'
};

function StatCard({ title, value, subtitle, accent = '#34d399' }) {
  return (
    <div style={{ ...cardBaseStyle, position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `radial-gradient(circle at 20% 20%, ${accent}15, transparent 35%)`
      }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <p style={{ margin: 0, color: '#94a3b8', fontSize: 14, letterSpacing: 0.5 }}>{title}</p>
        <h3 style={{ margin: '10px 0 6px', fontSize: 28, color: '#f8fafc' }}>{value}</h3>
        <p style={{ margin: 0, color: '#cbd5e1', fontSize: 13 }}>{subtitle}</p>
      </div>
    </div>
  );
}

function BarChart({ title, data, dataKey, labelKey, color }) {
  const maxValue = useMemo(
    () => Math.max(...data.map(item => item[dataKey] || 0), 1),
    [data, dataKey]
  );

  return (
    <div style={{ ...cardBaseStyle, background: '#0b1221', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 999, background: color }}></span>
        <p style={{ margin: 0, color: '#e2e8f0', fontWeight: 600, fontSize: 15 }}>{title}</p>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 200 }}>
        {data.map((item, index) => {
          const value = item[dataKey] || 0;
          const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
          
          return (
            <div 
              key={`${item[labelKey]}-${index}`} 
              style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8
              }}
            >
              {/* Valeur au-dessus */}
              {value > 0 && (
                <div style={{ height: 20, display: 'flex', alignItems: 'center' }}>
                  <span style={{ 
                    color: color, 
                    fontSize: 12, 
                    fontWeight: 700
                  }}>
                    {value}
                  </span>
                </div>
              )}
              {value === 0 && <div style={{ height: 20 }} />}
              
              {/* Barre */}
              <div style={{ 
                width: '100%',
                height: 150,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center'
              }}>
                {value > 0 ? (
                  <div
                    style={{
                      width: '80%',
                      maxWidth: 50,
                      height: `${heightPercent}%`,
                      background: `linear-gradient(180deg, ${color}, ${color}80)`,
                      borderRadius: '8px 8px 4px 4px',
                      boxShadow: `0 4px 12px ${color}40`,
                      border: `1px solid ${color}30`
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '80%',
                      maxWidth: 50,
                      height: 4,
                      background: 'rgba(71, 85, 105, 0.3)',
                      borderRadius: 2
                    }}
                  />
                )}
              </div>
              
              {/* Label */}
              <div style={{ height: 28 }}>
                <p style={{ 
                  margin: 0,
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: 10,
                  lineHeight: 1.2
                }}>
                  {item[labelKey].replace('Semaine du ', '')}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ValuePill({ label, value }) {
  return (
    <div style={{
      display: 'inline-flex',
      gap: 8,
      alignItems: 'center',
      padding: '8px 12px',
      background: 'rgba(52, 211, 153, 0.1)',
      borderRadius: 999,
      color: '#34d399',
      fontWeight: 600,
      fontSize: 13
    }}>
      <span>{label}</span>
      <span style={{ color: '#ecfeff' }}>{value}</span>
    </div>
  );
}

function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await axiosInstance.get('/admin/dashboard/overview');
        console.log('📊 Dashboard data:', response.data);
        setMetrics(response.data);
      } catch (err) {
        console.error('❌ Dashboard error:', err);
        setError("Impossible de charger le tableau de bord. Merci de réessayer.");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const formatter = new Intl.NumberFormat('fr-FR');
  const normalizeNumber = value => (Number.isFinite(value) ? value : 0);
  const currency = value => `${formatter.format(normalizeNumber(value))} €`;
  const formatCount = value => formatter.format(normalizeNumber(value));
  const percent = value => `${(normalizeNumber(value) * 100).toFixed(0)}%`;

  const highlight = useMemo(() => {
    if (!metrics || !metrics.trends) return null;
    const maxRevenueWeek = metrics.trends.reduce(
      (acc, item) => (item.combined_margin > acc.combined_margin ? item : acc),
      { combined_margin: 0 }
    );
    return maxRevenueWeek.combined_margin > 0 ? maxRevenueWeek : null;
  }, [metrics]);

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ color: '#e2e8f0', textAlign: 'center', padding: '120px 0' }}>
          <div style={{
            width: 48,
            height: 48,
            margin: '0 auto 18px',
            borderRadius: '50%',
            border: '4px solid #1f2937',
            borderTopColor: '#10b981',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: '#94a3b8' }}>Chargement du tableau de bord...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '120px 0' }}>
          {error}
        </div>
      );
    }

    if (!metrics) return null;

    const orders = metrics.financial?.orders || {};
    const donations = metrics.financial?.donations || {};
    const combined = metrics.financial?.combined || {};
    const conversion = metrics.conversion || {};
    const retention = metrics.retention || {};

    return (
      <>
        {/* HERO SECTION */}
        <section style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, alignItems: 'stretch' }}>
          <div style={{ ...cardBaseStyle, padding: 28 }}>
            <h1 style={{ color: '#f8fafc', margin: '12px 0 8px', fontSize: 32 }}>Tableau de bord CSIBON</h1>
            {highlight && (
              <ValuePill
                label="🏆 Meilleure semaine CA"
                value={`${highlight.label} · ${currency(highlight.combined_margin)}`}
              />
            )}
          </div>
          <div style={{ ...cardBaseStyle, background: 'linear-gradient(135deg, #0b8c6c, #047857)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10 }}>
            <p style={{ margin: 0, color: '#d1fae5', letterSpacing: 0.5 }}>KPI usage</p>
            <h2 style={{ margin: 0, color: '#ecfeff', fontSize: 34 }}>{formatCount(metrics.usage.total_users)} usagers</h2>
            <p style={{ margin: 0, color: '#d1fae5' }}>+ {formatCount(metrics.usage.new_users_week)} nouveaux sur les 7 derniers jours</p>
            <p style={{ margin: 0, color: '#ecfeff' }}>Moyenne d'usages / utilisateur : {metrics.usage.usage_per_user}</p>
          </div>
        </section>

        {/* STATISTIQUES UTILISATEURS */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, marginTop: 24 }}>
          <StatCard
            title="Nbr d'usagers total"
            value={formatCount(metrics.usage.total_users)}
            subtitle="Population CSIBON"
          />
          <StatCard
            title="Nvx usagers / semaine"
            value={`+${formatCount(metrics.usage.new_users_week)}`}
            subtitle="Arrivées récentes"
            accent="#f59e0b"
          />
          <StatCard
            title="Nbr d'usage / semaine"
            value={formatCount(metrics.usage.weekly_usage)}
            subtitle="Commandes validées (hors tests)"
            accent="#38bdf8"
          />
          <StatCard
            title="Nbr d'usage / utilisateur"
            value={metrics.usage.usage_per_user}
            subtitle="Moyenne globale"
            accent="#c084fc"
          />
        </section>

        {/* NOUVELLES MÉTRIQUES: CONVERSION ET RÉTENTION */}
        <h2 style={{ color: '#a78bfa', marginTop: 32, marginBottom: 16, fontSize: 24 }}>
          🎯 Conversion & Rétention
        </h2>
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          <StatCard
            title="Taux de conversion global"
            value={`${conversion.conversion_rate || 0}%`}
            subtitle={`${formatCount(conversion.users_with_orders || 0)} utilisateurs actifs sur ${formatCount(metrics.usage.total_users)}`}
            accent="#a78bfa"
          />
          <StatCard
            title="Conversion cette semaine"
            value={`${conversion.conversion_rate_week || 0}%`}
            subtitle={`${formatCount(conversion.new_users_who_ordered_week || 0)} nvx utilisateurs ont commandé`}
            accent="#c084fc"
          />
          <StatCard
            title="Taux de rétention"
            value={`${retention.retention_rate || 0}%`}
            subtitle={`${formatCount(retention.users_with_multiple_orders || 0)} utilisateurs récurrents (2+ commandes)`}
            accent="#10b981"
          />
          <StatCard
            title="Utilisateurs ayant passé une seule commande"
            value={formatCount(retention.one_time_users || 0)}
            subtitle="N'ont commandé qu'une seule fois"
            accent="#f59e0b"
          />
          <StatCard
            title="Retours cette semaine"
            value={formatCount(retention.returning_users_this_week || 0)}
            subtitle="Utilisateurs qui reviennent"
            accent="#34d399"
          />
        </section>

        {/* STATISTIQUES HORECA */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, marginTop: 24 }}>
          <StatCard
            title="Établissements CSIBON"
            value={formatCount(metrics.horeca.csibon_establishments)}
            subtitle="Actifs sur la plateforme"
          />
          <StatCard
            title="Nvx établissements"
            value={`+${formatCount(metrics.horeca.new_csibon_establishments)}`}
            subtitle="Sur les 7 derniers jours"
            accent="#f87171"
          />
        </section>

        {/* SECTION FINANCIÈRE - COMMANDES */}
        <h2 style={{ color: '#34d399', marginTop: 32, marginBottom: 16, fontSize: 24 }}>
          💰 Commandes
        </h2>
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          <StatCard
            title="Volume commandes (total)"
            value={currency(orders.revenue_total)}
            subtitle={`${formatCount(orders.count_total)} commandes (dont ${percent(orders.commission_rate)} pour vous)`}
            accent="#22d3ee"
          />
          <StatCard
            title="Volume commandes (semaine)"
            value={currency(orders.revenue_week)}
            subtitle={`${formatCount(orders.count_week)} commandes`}
            accent="#38bdf8"
          />
          <StatCard
            title="💵 Votre CA commandes (total)"
            value={currency(orders.margin_total)}
            subtitle={`Commission ${percent(orders.commission_rate)} sur ${currency(orders.revenue_total)}`}
            accent="#10b981"
          />
          <StatCard
            title="💵 Votre CA commandes (semaine)"
            value={currency(orders.margin_week)}
            subtitle={`Commission ${percent(orders.commission_rate)} sur ${currency(orders.revenue_week)}`}
            accent="#34d399"
          />
        </section>

        {/* SECTION FINANCIÈRE - DONS */}
        <h2 style={{ color: '#f59e0b', marginTop: 32, marginBottom: 16, fontSize: 24 }}>
          🎁 Dons
        </h2>
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          <StatCard
            title="Volume dons (total)"
            value={currency(donations.amount_total)}
            subtitle={`${formatCount(donations.count_total)} dons (dont ${percent(donations.commission_rate)} pour vous)`}
            accent="#f59e0b"
          />
          <StatCard
            title="Volume dons (semaine)"
            value={currency(donations.amount_week)}
            subtitle={`${formatCount(donations.count_week)} dons`}
            accent="#fb923c"
          />
          <StatCard
            title="💵 Votre CA dons (total)"
            value={currency(donations.margin_total)}
            subtitle={`Commission ${percent(donations.commission_rate)} sur ${currency(donations.amount_total)}`}
            accent="#10b981"
          />
          <StatCard
            title="💵 Votre CA dons (semaine)"
            value={currency(donations.margin_week)}
            subtitle={`Commission ${percent(donations.commission_rate)} sur ${currency(donations.amount_week)}`}
            accent="#34d399"
          />
        </section>

        {/* SECTION FINANCIÈRE - CONSOLIDÉ */}
        <h2 style={{ color: '#10b981', marginTop: 32, marginBottom: 16, fontSize: 24 }}>
          📊 Vue consolidée
        </h2>
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          <StatCard
            title="Volume total plateforme"
            value={currency(combined.revenue_total)}
            subtitle={`Somme des transactions (${currency(orders.revenue_total)} + ${currency(donations.amount_total)})`}
            accent="#64748b"
          />
          <StatCard
            title="Volume plateforme (semaine)"
            value={currency(combined.revenue_week)}
            subtitle={`Sur les 7 derniers jours`}
            accent="#94a3b8"
          />
          <StatCard
            title="💵 VOTRE CA TOTAL"
            value={currency(combined.margin_total)}
            subtitle={`Vos commissions (${currency(orders.margin_total)} + ${currency(donations.margin_total)})`}
            accent="#10b981"
          />
          <StatCard
            title="💵 VOTRE CA SEMAINE"
            value={currency(combined.margin_week)}
            subtitle={`Sur les 7 derniers jours`}
            accent="#34d399"
          />
          <StatCard
            title="CA / usager"
            value={currency(combined.margin_per_user)}
            subtitle={`Rentabilité moyenne par utilisateur`}
            accent="#a78bfa"
          />
          <StatCard
            title="Volume / usager"
            value={currency(combined.revenue_per_user)}
            subtitle={`Transactions moyennes par utilisateur`}
            accent="#c084fc"
          />
        </section>

        {/* GRAPHIQUES DE TENDANCE */}
        <h2 style={{ color: '#38bdf8', marginTop: 32, marginBottom: 16, fontSize: 24 }}>
          📈 Tendances hebdomadaires (8 dernières semaines)
        </h2>
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18 }}>
          <BarChart
            title="Nouveaux usagers par semaine"
            data={metrics.trends}
            dataKey="new_users"
            labelKey="label"
            color="#38bdf8"
          />
          <BarChart
            title="Usages validés par semaine"
            data={metrics.trends}
            dataKey="usages"
            labelKey="label"
            color="#f59e0b"
          />
          <BarChart
            title="📊 Taux conversion hebdo (%)"
            data={metrics.trends}
            dataKey="conversion_rate"
            labelKey="label"
            color="#a78bfa"
          />
          <BarChart
            title="🔄 Utilisateurs récurrents"
            data={metrics.trends}
            dataKey="returning_users"
            labelKey="label"
            color="#34d399"
          />
          <BarChart
            title="Volume commandes hebdo"
            data={metrics.trends}
            dataKey="orders_revenue"
            labelKey="label"
            color="#22d3ee"
          />
          <BarChart
            title="💵 CA commandes hebdo (10%)"
            data={metrics.trends}
            dataKey="orders_margin"
            labelKey="label"
            color="#10b981"
          />
          <BarChart
            title="Volume dons hebdo"
            data={metrics.trends}
            dataKey="donations_amount"
            labelKey="label"
            color="#fb7185"
          />
          <BarChart
            title="💵 CA dons hebdo (20%)"
            data={metrics.trends}
            dataKey="donations_margin"
            labelKey="label"
            color="#10b981"
          />
          <BarChart
            title="Volume total hebdo"
            data={metrics.trends}
            dataKey="combined_revenue"
            labelKey="label"
            color="#64748b"
          />
          <BarChart
            title="💵 VOTRE CA TOTAL HEBDO"
            data={metrics.trends}
            dataKey="combined_margin"
            labelKey="label"
            color="#10b981"
          />
        </section>
      </>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b1120' }}>
      <Navbar />
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 18px 48px' }}>
        {renderContent()}
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default AdminDashboard;